import { graphlib, layout } from "@dagrejs/dagre";
import { useCallback, useMemo } from "react";
import ReactFlow, {
  Background,
  Controls,
  type Edge,
  MarkerType,
  type Node,
} from "reactflow";
import "reactflow/dist/style.css";
import type { MapView } from "@/lib/core/schemas/map-view";
import { LocationNode, type LocationNodeData } from "./location-node";
import { PassageEdge, type PassageEdgeData } from "./passage-edge";

const NODE_WIDTH = 150;
const NODE_HEIGHT = 80;

const nodeTypes = { location: LocationNode };
const edgeTypes = { passage: PassageEdge };

const layoutNodes = (
  nodes: Node<LocationNodeData>[],
  edges: Edge[]
): Node<LocationNodeData>[] => {
  const g = new graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "TB", nodesep: 60, ranksep: 80 });
  for (const node of nodes) {
    g.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  }
  for (const edge of edges) {
    g.setEdge(edge.source, edge.target);
  }
  layout(g);
  return nodes.map((node) => {
    const { x, y } = g.node(node.id);
    return {
      ...node,
      position: { x: x - NODE_WIDTH / 2, y: y - NODE_HEIGHT / 2 },
    };
  });
};

export type MapGraphProps = {
  mapView: MapView;
  onNodeClick: (nodeId: string) => void;
};

export const MapGraph = ({ mapView, onNodeClick }: MapGraphProps) => {
  const { nodes, edges } = useMemo(() => {
    const rfNodes: Node<LocationNodeData>[] = mapView.nodes.map((n) => ({
      id: n.id,
      type: "location",
      data: { ...n, isPartyLocation: mapView.partyLocatedIn === n.id },
      position: { x: 0, y: 0 }, // overwritten by layout
    }));
    const rfEdges: Edge<PassageEdgeData>[] = mapView.edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      type: "passage",
      data: {
        kind: e.kind,
        directionForward: e.directionForward,
        directionReverse: e.directionReverse,
      },
      markerEnd: { type: MarkerType.ArrowClosed, color: "#9ca3af" },
    }));
    return { nodes: layoutNodes(rfNodes, rfEdges), edges: rfEdges };
  }, [mapView]);

  const handleNodeClick = useCallback(
    (_evt: React.MouseEvent, node: Node) => onNodeClick(node.id),
    [onNodeClick]
  );

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <ReactFlow
        edges={edges}
        edgeTypes={edgeTypes}
        elementsSelectable
        fitView
        nodes={nodes}
        nodesConnectable={false}
        nodesDraggable={false}
        nodeTypes={nodeTypes}
        onNodeClick={handleNodeClick}
      >
        <Background />
        <Controls />
      </ReactFlow>
    </div>
  );
};
